import React, { FC, useEffect, useState } from 'react';

import * as analytics from '@adsk/acs-web-analytics';
import { Theme } from '@adsk/alloy-react-box';
import { LocaleProvider } from '@adsk/alloy-react-locale';
import { RemotesProvider } from '@adsk/exo-skeleton-react';

import { FullPageLayoutLoader } from './components/FullPageLayoutLoader/FullPageLayoutLoader';
import { IdleTimerProvider } from './components/IdleTimer/providers/IdleTimerProvider';
import { LaunchDarklyProvider } from './components/LaunchDarklyProvider';
import Router from './components/Router/Router';
import { ServiceWorkerContainer } from './components/ServiceWorkerContainer';
import { loadEnvConfig } from './envs/envConfig';
import RemoteOverridesProvider from './providers/RemoteOverridesProvider';
import { getRawRemoteOverride } from './utils/getRemoteOverrides';
import { initi18n } from './utils/i18n';
import { disposeMonitoringContext } from './utils/monitoring';
import { transformRemotesUrl } from './utils/transformRemotesUrl';

const App: FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const init = async () => {
      analytics.waitForBaseProperties('account_id');
      analytics.waitForBaseProperties('license');

      await Promise.all([initi18n(), loadEnvConfig()]);

      if (!window.skipAlloyAnalyticsLoad) {
        analytics.load();
      }
      setIsLoaded(true);
    };

    void init();

    return () => {
      disposeMonitoringContext();
    };
  }, []);

  return (
    <Theme>
      <LaunchDarklyProvider>
        <ServiceWorkerContainer>
          <RemotesProvider
            getRemoteOverride={getRawRemoteOverride}
            transformRemotesUrl={transformRemotesUrl}
          >
            <RemoteOverridesProvider>
              <LocaleProvider>
                <IdleTimerProvider>
                  {isLoaded ? <Router /> : <FullPageLayoutLoader />}
                </IdleTimerProvider>
              </LocaleProvider>
            </RemoteOverridesProvider>
          </RemotesProvider>
        </ServiceWorkerContainer>
      </LaunchDarklyProvider>
    </Theme>
  );
};

export default App;
